/**
 * Provenance d'un paiement : les paramètres du lien de départ (utm_*, gclid,
 * fbclid, paramètres perso de l'association…), repris dans les webhooks que
 * Donoor envoie à l'association.
 *
 * Aucun cookie. La provenance vit en mémoire de page, doublée du stockage de
 * SESSION de l'onglet (survit au rechargement imposé par un prestataire de
 * paiement, effacé à la fermeture de l'onglet). Pour traverser un NOUVEL
 * onglet, elle voyage dans le lien lui-même, sous UN seul paramètre `prov`
 * (jamais des utm_* sur un lien interne : ça fausserait les statistiques de
 * visite de Donoor).
 *
 * Le tri strict (données sensibles, longueurs) est refait par l'API ; ici on
 * écarte seulement les paramètres techniques de Donoor et des prestataires.
 */

/** Paramètre porteur de la provenance sur les liens internes. */
export const PARAM_PROVENANCE = "prov";
/** Stockage de session : site de l'association qui affiche le widget. */
export const CLE_SESSION_HOTE_WIDGET = "donoor_widget_hote";

/** Page d'arrivée sur le site de l'association (widget). */
export const PARAM_PAGE_PROVENANCE = "prov_page";

const MAX_PARAMETRES = 25;

const CLES_TECHNIQUES = new Set([
  PARAM_PROVENANCE, PARAM_PAGE_PROVENANCE,
  "embed", "host", "mode", "primary", "layout", "w", "amounts", "steps", "bg", "text",
  "hidegoal", "hidelogo", "hidetitle", "hidetip", "hidefees", "hidefiscal", "hidesecure",
  "hiderecurring", "lang", "locale", "montant", "amount", "frequence", "recurring",
  "payment_intent", "payment_intent_id", "payment_intent_client_secret", "redirect_status",
  "setup_intent", "setup_intent_client_secret", "transaction_id", "paypal_order_id",
  "subscription_id", "order_id", "token", "payerid", "ba_token", "email", "_gl", "demo",
  "preview", "ref_page",
]);

export interface Provenance {
  parametres: Record<string, string>;
  pageArrivee: string | null;
}

/** Paramètres de provenance présents dans une query de route. */
export function parametresDeProvenance(query: Record<string, unknown>): Record<string, string> {
  const propres: Record<string, string> = {};
  for (const [cle, brut] of Object.entries(query)) {
    if (Object.keys(propres).length >= MAX_PARAMETRES) break;
    if (CLES_TECHNIQUES.has(cle.toLowerCase())) continue;
    const valeur = Array.isArray(brut) ? brut[0] : brut;
    if (typeof valeur !== "string" || !valeur.trim()) continue;
    propres[cle] = valeur.trim();
  }
  return propres;
}

/** `prov` → paramètres (contenu = une query string encodée). */
export function decoderProvenance(prov: unknown): Record<string, string> {
  if (typeof prov !== "string" || !prov) return {};
  try {
    return parametresDeProvenance(Object.fromEntries(new URLSearchParams(prov)));
  } catch {
    return {};
  }
}

/** Paramètres → valeur de `prov`. */
export function encoderProvenance(parametres: Record<string, string>): string {
  return new URLSearchParams(parametres).toString();
}

/**
 * Références du paiement lisibles dans la query d'une page de confirmation
 * (Stripe, PayPal, numéro de commande).
 */
export function referencesDeConfirmation(query: Record<string, unknown>): string[] {
  const refs = ["payment_intent_id", "payment_intent", "paypal_order_id", "subscription_id", "order_id"]
    .map((cle) => query[cle])
    .map((v) => (Array.isArray(v) ? v[0] : v))
    .filter((v): v is string => typeof v === "string" && v.length >= 10);
  return [...new Set(refs)];
}
